<template lang="pug">
  AppModal(:value="showModal" @input="onClose" :title="'Editar paquete ' + packageCode")
    template
      .notice.notice--danger(v-if="errorMsg")
        | {{ errorMsg }}
      form(@submit.prevent="onSubmit")
        .form-grid
          AppField(label="Nombre del destinatario" required :state="state('customerName')" :invalid-feedback="feedback('customerName')")
            AppInput(v-model="form.customerName" placeholder="Nombre del destinatario")
          AppField(label="Teléfono del destinatario" required :state="state('customerPhone')" :invalid-feedback="phoneFeedback()")
            AppInput(v-model="form.customerPhone" placeholder="Ej: 912345678")
          AppField(label="Correo (opcional)")
            AppInput(v-model="form.customerEmail" placeholder="correo@ejemplo.cl" type="email")
          AppField(label="Dirección de destino" required :state="state('customerAddress')" :invalid-feedback="feedback('customerAddress')")
            AppInput(v-model="form.customerAddress" placeholder="Dirección de destino")
          AppField(label="Comuna de destino" required :state="state('customerCommune')" :invalid-feedback="feedback('customerCommune')")
            AppSelect(v-model="form.customerCommune" :options="communesOptions" @change="onCommuneChange")
              template(#first)
                option(disabled value="") Seleccionar
          AppField(label="Tamaño del paquete" required :state="state('size')" :invalid-feedback="feedback('size')")
            AppSelect(v-model="form.size" :options="sizeOptions" @change="onSizeChange")
              template(#first)
                option(disabled value="") Seleccionar
            div(v-if="serviceMissing" class="service-missing") Servicio no disponible. Selecciona otro.
          AppField(label="Cantidad de bultos" required :state="state('bundleQuantity')" :invalid-feedback="feedback('bundleQuantity')")
            AppInput(v-model="form.bundleQuantity" type="number" min="1" max="25")
          AppField(label="Tags")
            AppSelect(v-model="form.tags" :options="tagsOptions")
          AppField(label="ID Externo (opcional)")
            AppInput(v-model="form.externalId" placeholder="ID externo")
          AppField(label="ID Mercado Flex" :required="isFlex" :state="state('labelCode')" :invalid-feedback="feedback('labelCode')")
            AppInput(v-model="form.labelCode" placeholder="ID Mercado Flex")
        AppField(label="Observaciones")
          AppInput(v-model="form.observations" maxlength="500")
        .form-footer
          div.cost
            span.font-weight-bold Costo de envío:
            span.cost-value ${{ previewShippingCost === null ? '—' : previewShippingCost.toLocaleString('es-CL') }}
    template(#footer)
      AppButton(variant="secondary" outline @click="onClose") Cancelar
      AppButton(variant="success" :disabled="saving" @click="onSubmit") {{ saving ? 'Guardando...' : 'Guardar' }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { required, minValue, maxValue } from 'vuelidate/lib/validators'
import AppModal from '@/components/UI/AppModal.vue'
import AppField from '@/components/UI/AppField.vue'
import AppInput from '@/components/UI/AppInput.vue'
import AppSelect from '@/components/UI/AppSelect.vue'
import AppButton from '@/components/UI/AppButton.vue'
import { validPhoneNumber } from '../../utils/customValidations'
import { cleanPhone } from '../../utils/cleanData'

export default {
  name: 'EditPackageForm',
  components: {
    AppModal,
    AppField,
    AppInput,
    AppSelect,
    AppButton
  },
  props: {
    packageItem: { type: Object, default: null },
    storeData: { type: Object, default: null },
    canEdit: { type: Boolean, default: false },
    editWindowReason: { type: String, default: '' }
  },
  data() {
    return {
      showModal: false,
      saving: false,
      errorMsg: '',
      form: {
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerAddress: '',
        customerCommune: '',
        size: '',
        bundleQuantity: 1,
        observations: '',
        tags: '',
        externalId: null,
        labelCode: null
      },
      communes: [],
      specialPrices: [],
      availableServices: [],
      flexEnabled: false,
      flexSmallPrice: 0,
      flexMediumPrice: 0,
      serviceMissing: false,
      previewShippingCost: null,
      storeRegionId: null,
      packageId: null,
      packageCode: null
    }
  },
  validations() {
    return {
      form: {
        customerName: { required },
        customerPhone: { required, validPhoneNumber },
        customerAddress: { required },
        customerCommune: { required },
        size: { required },
        bundleQuantity: { required, minValue: minValue(1), maxValue: maxValue(25) },
        labelCode: { requiredForFlex: (value) => this.isFlex ? required(value) : true }
      }
    }
  },
  computed: {
    ...mapGetters(['user', 'regions']),
    isFlex() {
      return this.form.tags === 'flex'
    },
    idStore() {
      if (this.user.role === 'admin') {
        return this.storeData && this.storeData.id
      }
      return this.user.storeId
    },
    communesOptions() {
      return this.communes
        .filter(c => c.region !== null && c.region !== undefined)
        .map(c => ({ value: String(c.id), text: c.name }))
    },
    sizeOptions() {
      const services = this.availableServices || []
      const custom = services
        .filter(s => s.code !== 'small' && s.code !== 'medium')
        .map(s => ({ value: s.name, text: s.name }))
      const legacy = [
        { value: 'small', text: 'PEQUEÑO' },
        { value: 'medium', text: 'MEDIANO' }
      ]
      return legacy.concat(custom)
    },
    tagsOptions() {
      const options = [
        { value: '', text: 'N/A' },
        { value: 'exchange', text: 'Cambio' },
        { value: 'return', text: 'Devolución' }
      ]
      if (this.flexEnabled) {
        options.push({ value: 'flex', text: 'Flex' })
      }
      return options
    }
  },
  methods: {
    ...mapActions(['listAllActiveCommunes', 'listRegion', 'getRegionsStore', 'getRegionByCommune', 'fetchAvailableShippingServices']),
    cleanPhone,
    show(packageItem) {
      const item = packageItem || this.packageItem
      this.$v.$reset()
      this.errorMsg = ''
      this.saving = false
      this.serviceMissing = false
      this.packageId = item && item.id
      this.packageCode = item && item.code
      const initialCommune = item.customerCommune || (item.commune && item.commune.id) || ''
      this.form = {
        customerName: item.customerName || '',
        customerPhone: item.customerPhone || '',
        customerEmail: item.customerEmail || '',
        customerAddress: item.customerAddress || '',
        customerCommune: initialCommune ? String(initialCommune) : '',
        size: this.normalizeSize(item.size),
        bundleQuantity: item.bundleQuantity || 1,
        observations: item.observations || '',
        tags: item.tags || '',
        externalId: item.externalId || null,
        labelCode: item.labelCode || null
      }
      this.showModal = true
      this.$nextTick(async () => {
        await this.loadData()
        this.computePreview()
      })
    },
    onClose() {
      this.showModal = false
    },
    normalizeSize(size) {
      if (size === 'PEQUEÑO') return 'small'
      if (size === 'MEDIANO') return 'medium'
      return size || ''
    },
    state(field) {
      const v = this.$v.form[field]
      if (!v || !v.$dirty) return null
      return !v.$invalid
    },
    feedback(field) {
      const v = this.$v.form[field]
      if (!v || !v.$dirty || !v.$invalid) return ''
      const messages = {
        customerName: 'Este campo es requerido',
        customerAddress: 'Este campo es requerido',
        customerCommune: 'Este campo es requerido',
        size: 'Este campo es requerido',
        bundleQuantity: 'Debe estar entre 1 y 25',
        labelCode: 'Este campo es requerido para envíos Flex'
      }
      return messages[field] || 'Valor inválido'
    },
    phoneFeedback() {
      const v = this.$v.form.customerPhone
      if (!v || !v.$dirty || !v.$invalid) return ''
      if (!v.required) return 'Este campo es requerido'
      if (!v.validPhoneNumber) return 'Debe iniciar con 9 o 2 y tener 9 dígitos, por ejemplo: 912345678 o 212345678'
      return 'Teléfono inválido'
    },
    async loadData() {
      this.communes = await this.listAllActiveCommunes()
      await this.listRegion()
      this.specialPrices = (await this.getRegionsStore({ idStore: this.idStore })).data || []

      const withdrawalCommuneId = this.storeData && this.storeData.withdrawalCommuneId
      if (withdrawalCommuneId) {
        const region = await this.getRegionByCommune(withdrawalCommuneId)
        this.storeRegionId = region ? region.id : null
        this.flexEnabled = !!(region && region.isFlexShippingEnabled)
        const special = this.specialPrices.find(p => p.idRegion === this.storeRegionId)
        this.flexSmallPrice = (special && special.flexSmallShippingPrice != null)
          ? special.flexSmallShippingPrice
          : (region ? region.flexSmallShippingPrice : 0)
        this.flexMediumPrice = (special && special.flexMediumShippingPrice != null)
          ? special.flexMediumShippingPrice
          : (region ? region.flexMediumShippingPrice : 0)
      }

      const selectedCommune = this.currentCommune()
      if (selectedCommune && selectedCommune.regionId) {
        await this.loadAvailableServices(selectedCommune.regionId)
      }
    },
    async loadAvailableServices(regionId) {
      const res = await this.fetchAvailableShippingServices({ storeId: this.idStore, regionId })
      this.availableServices = (res && res.data) || res || []
    },
    currentCommune() {
      return this.communes.find(c => String(c.id) === String(this.form.customerCommune)) || null
    },
    async onCommuneChange() {
      this.serviceMissing = false
      const selectedCommune = this.currentCommune()
      if (selectedCommune && selectedCommune.regionId) {
        await this.loadAvailableServices(selectedCommune.regionId)
      }
      this.computePreview()
    },
    onSizeChange() {
      this.computePreview()
    },
    computePreview() {
      this.previewShippingCost = null
      this.serviceMissing = false
      const commune = this.currentCommune()
      const size = this.form.size
      if (!commune || !size) {
        return
      }
      const regionId = commune.regionId
      const isFlex = this.isFlex
      const special = this.specialPrices.find(p => p.idRegion === regionId)
      const region = this.regions.find(r => r.id === regionId)

      if (size === 'small' || size === 'medium') {
        if (isFlex && this.flexEnabled) {
          const specialPrice = size === 'small' ? special && special.flexSmallShippingPrice : special && special.flexMediumShippingPrice
          const defaultPrice = size === 'small' ? this.flexSmallPrice : this.flexMediumPrice
          this.previewShippingCost = (specialPrice && specialPrice > 0) ? specialPrice : (defaultPrice || 0)
        } else {
          const specialPrice = size === 'small' ? special && special.smallShippingPrice : special && special.mediumShippingPrice
          const defaultPrice = size === 'small' ? region && region.smallShippingPrice : region && region.mediumShippingPrice
          this.previewShippingCost = (specialPrice && specialPrice > 0) ? specialPrice : (defaultPrice || 0)
        }
      } else {
        const service = (this.availableServices || []).find(s => s.name === size)
        if (service && typeof service.price === 'number') {
          this.previewShippingCost = service.price
        } else {
          this.previewShippingCost = 0
          this.serviceMissing = true
        }
      }
    },
    async onSubmit() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      if (this.serviceMissing) {
        this.errorMsg = 'El servicio de envío seleccionado no está disponible. Selecciona otro tamaño.'
        return
      }
      this.saving = true
      this.errorMsg = ''
      const data = {
        customerName: this.form.customerName,
        customerPhone: cleanPhone(this.form.customerPhone),
        customerEmail: this.form.customerEmail || '',
        customerAddress: this.form.customerAddress,
        customerCommune: Number(this.form.customerCommune),
        size: this.form.size,
        bundleQuantity: Number(this.form.bundleQuantity),
        observations: this.form.observations || '',
        tags: this.form.tags || null,
        externalId: this.form.externalId || null,
        labelCode: this.form.labelCode || null
      }
      try {
        const res = await this.$store.dispatch('editPackage', { id: this.packageId, data })
        if (res && res.status && res.status >= 400) {
          const backendMsg = res.data && (res.data.error || res.data.message)
          this.errorMsg = backendMsg || 'No se pudo actualizar el paquete'
          this.saving = false
          return
        }
        this.$root.$bvToast.toast('Paquete actualizado', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        })
        this.saving = false
        this.showModal = false
        this.$emit('done', res)
      } catch (error) {
        console.error(error)
        this.errorMsg = 'No se pudo actualizar el paquete'
        this.saving = false
      }
    }
  },
  watch: {
    'form.tags'() {
      this.computePreview()
    }
  }
}
</script>

<style lang="scss" scoped>
.notice {
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  margin-bottom: 1rem;

  &--warning {
    background: #fff7ed;
    border: 1px solid #fed7aa;
    color: #9a3412;
  }

  &--danger {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 1rem;
}

.service-missing {
  color: var(--status-rejected, #f46a6a);
  font-size: 12px;
  margin-top: 0.25rem;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin: 0.5rem 0 0.25rem;

  .cost-value {
    font-weight: 700;
    margin-left: 0.5rem;
  }
}
</style>
